import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, ButtonRow, DangerButton } from "../../components/button";
import { Card, CardContent } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DateTimeLabel } from "../../components/datetime-label";
import { Icon, IconFrame } from "../../components/icon";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../components/table";
import { CopiableText, InlineCode, MutedText, Paragraph } from "../../components/text";
import { formatFlexibibleCurrency, formatFloat, formatNumber, formatTokenCount, formatVeryPreciseCurrency } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { cond, ellipsis, find, pageQuery } from "../../util";
import { ModelBadge, ProviderBadge } from "../../components/model-icon";
import { FormControl, Select, TextInput } from "../../components/form";
import { throttledCall } from "../../ui/throttle";
import { copyToClipboard } from "../../ui/clipboard";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { showFlyout } from "../../ui/flyout";
import { TokenLabel } from "../../components/token-label";
import { getSession } from "../../session";
import { displayFormControl } from "../../ui/alert";
import { SpecializedControl } from "../../components/specializedObject";
import { RelativeTimeRangePicker } from "../../components/relative-time-range";
import { Badge, LiveConversationStatusBadge, TagBadge } from "../../components/badge";
import { Constants } from "../../constants";

function ConversationOrigin(origin) {
    return cond(
        [origin[0] == "ChatCompletionsApi", el.fragment(IconFrame(Icon("terminal-line"), { style: { color: "#549708ff" } }), "Chat completions API")],
        [origin[0] == "SessionsApi", el.fragment(IconFrame(Icon("discuss-line"), { style: { color: "#a51073ff" } }), "Sessions API")],
        [origin[0] == "FunctionsApi", el.fragment(IconFrame(Icon("braces-line"), { style: { color: "#089773ff" } }), "Functions API")],
    );
}

export default function (ctx) {

    const filter = el.state.create(ctx.query.queryFilter || "");
    const conversations = el.state.create(null);
    const live = el.state.create([]);
    const completedLiveIds = new Set();
    const eventSource = api.createEventSource("/api/v1/conversations/management/live");
    const timeRange = RelativeTimeRangePicker({
        onInput() {
            runOperation("Loading conversations...", refresh);
        }
    });

    async function refresh() {
        const result = await api.getJson("/api/v1/conversations?" + new URLSearchParams({
            timeStart: timeRange.value.start,
            timeEnd: timeRange.value.end,
            filter: filter.value
        }));
        conversations.value = result.data;
    }

    runOperation("Loading conversations...", async () => {
        await refresh();
    });

    filter.subscribe(refresh);

    eventSource.onmessage = event => {
        live.value = JSON.parse(event.data).data;
        const completed = live.value.filter(conv => conv.status == "completed" && !completedLiveIds.has(conv.id));
        if (completed.length) {
            completed.forEach(conv => completedLiveIds.add(conv.id));
            timeRange.value = { start: timeRange.value.start, end: new Date().toISOString() };
            refresh();
        }
    };

    return Dashboard(
        {
            classList: ["full-size"],
            mount(element) {
                const observer = new MutationObserver(() => {
                    if (!element.isConnected) {
                        eventSource.close();
                        observer.disconnect();
                    }
                });
                observer.observe(document.body, { childList: true, subtree: true });
            }
        },
        DashboardTitle(
            "Conversations",
            "Here you view the latest conversations held in your account."
        ),
        Card(
            CardContent(
                CommandBar(
                    FormControl("Search",
                        TextInput("searchInput", "Filter conversations...", {
                            onInput(event) {
                                throttledCall(() => {
                                    filter.value = event.target.value;
                                }, 300);
                            },
                            value: filter.value,
                            style: {
                                width: "400px"
                            }
                        })
                    ),
                    FormControl(null,
                        Button(Icon("question-line"), {
                            onClick() {
                                showFlyout(
                                    Paragraph("Search conversation logs using a search expression:"),
                                    el("ul",
                                        el("li",
                                            InlineCode("--model, -m"),
                                            " - conversations that involve a specific model."
                                        ),
                                        el("li",
                                            InlineCode("--user, -u"),
                                            " - conversations that involve a specific user (ExternalUserId)."
                                        ),
                                        el("li",
                                            InlineCode("--api-key"),
                                            " - conversations that involve a specific API key (key ID)."
                                        ),
                                        el("li",
                                            InlineCode("--gateway"),
                                            " - conversations that involve a specific gateway ID."
                                        ),
                                        el("li",
                                            InlineCode("--chat-client"),
                                            " - conversations that involve a specific chat client ID."
                                        ),
                                        el("li",
                                            InlineCode("--chat-session"),
                                            " - conversations that involve a specific chat session ID."
                                        )
                                    )
                                )
                            }
                        })
                    ),
                    FormControl("Time range", timeRange),
                    Button(Icon("refresh-line"), "Refresh", {
                        onClick() {
                            refresh();
                        }
                    }),
                    Button(Icon("download-line"), "Export all", {
                        onClick() {
                            displayFormControl({
                                title: "Export conversations",
                                message: Paragraph("Export conversations from your account within the period covered by your plan."),
                                //dialogSize: "medium",
                                formControlItem: SpecializedControl({
                                    value: {
                                        period: "7d",
                                        media: "text",
                                        thinking: "visible",
                                        truncate: "0"
                                    },
                                    fields: [
                                        {
                                            name: "period",
                                            label: "Period",
                                            control: Select({
                                                value: "7d",
                                                items: [
                                                    { value: "2h", label: "2 hours" },
                                                    { value: "1d", label: "1 day" },
                                                    { value: "7d", label: "7 days" },
                                                    { value: "30d", label: "30 days" }
                                                ]
                                            })
                                        },
                                        {
                                            name: "media",
                                            label: "Media",
                                            control: Select({
                                                value: "text",
                                                items: [
                                                    { value: "include", label: "Include" },
                                                    { value: "text", label: "Truncate to text" }
                                                ]
                                            })
                                        },
                                        {
                                            name: "thinking",
                                            label: "Thinking",
                                            control: Select({
                                                value: "visible",
                                                items: [
                                                    { value: "all", label: "All" },
                                                    { value: "visible", label: "Only visible" },
                                                    { value: "none", label: "None" }
                                                ]
                                            })
                                        },
                                        {
                                            name: "truncate",
                                            label: "Truncate",
                                            control: TextInput("truncateTokens", "0", {
                                                type: "number",
                                                value: "0",
                                                min: 0,
                                                max: 1000000,
                                                step: 1000
                                            })
                                        }
                                    ]
                                }),
                                validate: [
                                    [value => Number.isInteger(Number(value.truncate)) && Number(value.truncate) >= 0 && Number(value.truncate) <= 1000000, "Truncate must be between 0 and 1,000,000 tokens."]
                                ],
                                onConfirm(value) {
                                    const query = new URLSearchParams({
                                        period: value.period,
                                        media: value.media,
                                        thinking: value.thinking,
                                        truncate: Number(value.truncate || 0).toString(),
                                        "api-key": getSession().sessionKey
                                    });
                                    window.open(window.env.apiEndpoint + `/api/v1/conversations/management/export.jsonl?${query.toString()}`, "_blank");
                                }
                            });
                        }
                    })
                )
            ),
            CardContent(
                Table(
                    {
                        classList: ["hoverable", "hr-scrollable"],
                        pagination: {
                            perPage: 75
                        }
                    },
                    TableHeader(
                        TableHeaderCell(
                            "Origin"
                        ),
                        TableHeaderCell(
                            "Updated"
                        ),
                        TableHeaderCell(
                            "Model"
                        ),
                        TableHeaderCell(
                            "Provider"
                        ),
                        TableHeaderCell(
                            "Routing"
                        ),
                        TableHeaderCell(
                            "Variant"
                        ),
                        TableHeaderCell(
                            "Token count"
                        ),
                        TableHeaderCell(
                            "Cost"
                        ),
                        TableHeaderCell(
                            "Time"
                        ),
                        TableHeaderCell(
                            "First user message"
                        ),
                        TableHeaderCell(
                            "Last assistant message"
                        ),
                        TableHeaderActionCell(
                            "Actions"
                        )
                    ),
                    el.state.listening([live], () =>
                        TableBody(
                            {
                                classList: ["live"]
                            },
                            live.value.map(conv =>
                                TableRow(
                                    TableCell(
                                        ConversationOrigin(conv.origin)
                                    ),
                                    TableCell(
                                        LiveConversationStatusBadge(conv)
                                    ),
                                    TableCell(
                                        conv.modelName && ModelBadge(conv.modelName) || MutedText(Constants.Hyphen)
                                    ),
                                    TableCell(MutedText(Constants.Hyphen)),
                                    TableCell(MutedText(Constants.Hyphen)),
                                    TableCell(MutedText(Constants.Hyphen)),
                                    (conv.inputTokenCount != null && conv.outputTokenCount != null) && TableCell(
                                        TokenLabel(conv.inputTokenCount),
                                        MutedText(Icon("arrow-right-line")),
                                        TokenLabel(conv.outputTokenCount),
                                    ) || TableCell(MutedText(Constants.Hyphen)),
                                    TableCell(MutedText(Constants.Hyphen)),
                                    TableCell(
                                        formatFloat(conv.elapsedTime / 1000.0) + "s"
                                    ),
                                    TableCell(
                                        IconFrame(Icon("arrow-right-circle-line"), { style: { color: "#089771ff" } }),
                                        conv.inputPreview || MutedText("Other content")
                                    ),
                                    TableCell(
                                        IconFrame(Icon("arrow-left-circle-line"), { style: { color: "#ad5809" } }),
                                        conv.outputPreview || MutedText("Generating...")
                                    ),
                                    TableActionCell(
                                        (conv.status != "completed" || conv.conversationId) && Button(
                                            Icon("arrow-right-double-line"),
                                            "View",
                                            {
                                                onClick() {
                                                    page("/dashboard/conversations/" + (conv.conversationId || conv.id));
                                                }
                                            }
                                        )
                                    )
                                )
                            )
                        )
                    ),
                    el.state.listening([conversations], () =>
                        TableBody(
                            conversations.value?.map(conv =>
                                TableRow(
                                    TableCell(
                                        ConversationOrigin(conv.origin)
                                    ),
                                    TableCell(
                                        DateTimeLabel(conv.updatedAt)
                                    ),
                                    TableCell(
                                        conv.modelName && ModelBadge(conv.modelName, {
                                            classList: ["copiable"],
                                            onClick() {
                                                filter.value = find("searchInput").value = `--model ${conv.modelName}`;
                                                filter.requestUpdate();
                                            }
                                        })
                                    ),
                                    TableCell(
                                        conv.providerName && ProviderBadge(conv.providerName)
                                        || el.fragment(IconFrame(Icon("key-2-line")), "BYOK")
                                    ),
                                    TableCell(
                                        {
                                            Balanced: "Balanced",
                                            Cheapest: "Lowest cost",
                                            Fastest: "Fastest",
                                            Quality: "Highest quality",
                                            Special: "Special"
                                        }[conv.routingOption?.[0]] || MutedText(Constants.Hyphen)
                                    ),
                                    TableCell(
                                        conv.modelVariant && Badge(conv.modelVariant) || MutedText("Default")
                                    ),
                                    (conv.inputTokenCount != null && conv.outputTokenCount != null) && TableCell(
                                        TokenLabel(conv.inputTokenCount),
                                        MutedText(Icon("arrow-right-line")),
                                        TokenLabel(conv.outputTokenCount),
                                    ) || TableCell(MutedText(Constants.Hyphen)),
                                    conv.cost && TableCell(
                                        formatVeryPreciseCurrency(conv.cost),
                                        {
                                            title: conv.cost
                                        }
                                    ) || TableCell(MutedText(Constants.Hyphen)),
                                    conv.elapsedTime && TableCell(
                                        formatFloat(conv.elapsedTime / 1000.0) + "s"
                                    ) || TableCell(MutedText(Constants.Hyphen)),
                                    TableCell(
                                        IconFrame(Icon("arrow-right-circle-line"), { style: { color: "#089771ff" } }),
                                        conv.messagesPreview?.input || MutedText("Other content")
                                    ),
                                    conv.isError
                                    && TableCell(
                                        IconFrame(Icon("alert-line"), { style: { color: "#e04b4b" } }),
                                        MutedText("Error")
                                    )
                                    || TableCell(
                                        IconFrame(Icon("arrow-left-circle-line"), { style: { color: "#ad5809" } }),
                                        conv.messagesPreview?.output || MutedText("Other content")
                                    ),
                                    TableActionCell(
                                        ButtonRow(
                                            Button(
                                                Icon("arrow-right-double-line"),
                                                "View",
                                                {
                                                    onClick() {
                                                        page("/dashboard/conversations/" + conv.id);
                                                    }
                                                }
                                            ),
                                            Dropdown(null,
                                                DropdownItem("Delete conversation", Icon("delete-bin-line"), {
                                                    onClick() {
                                                        showFlyout(
                                                            Paragraph("Are you sure you want to delete this conversation? This action cannot be undone."),
                                                            DangerButton("Delete conversation", {
                                                                onClick() {
                                                                    runOperation("Deleting conversation...", async () => {
                                                                        await api.deleteJson(`/api/v1/conversations/${conv.id}`);
                                                                        refresh();
                                                                    })
                                                                }
                                                            })
                                                        )
                                                    }
                                                })
                                            )
                                        )
                                    )
                                )
                            )?.nullWhenEmpty() || DefaultRow("No conversations yet."),
                            {
                                mount() {
                                    this.closest("table")?.refreshPagination();
                                }
                            }
                        )
                    )
                )
            )
        )
    )
}
